import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, API, getTokens } from '../../../lib/api';
import { aplicarOps, compactar } from './operaciones';

/*
 * Guardado y coedición del editor de formularios.
 *
 * Cada cambio se aplica al momento en la copia local (optimista) y se encola; tras una pausa corta
 * (lo que dura escribir una palabra) se manda el lote a `POST /forms/{id}/cambios`. Solo hay UNA
 * petición en vuelo: así el orden de las operaciones es el que escribió la persona. Al volver, la
 * versión del servidor manda y encima se reaplican las operaciones escritas mientras viajaba.
 *
 * Los demás editores se ven por sondeo (`GET /forms/{id}/estado` cada 4 s, que también es el latido
 * de presencia): si su `rev` va por delante y aquí no hay nada pendiente, se relee el formulario.
 * Sin WebSocket a propósito: el del portal vive en el dominio del correo y un sondeo barato basta
 * para formularios (nadie teclea a la vez en la misma pregunta).
 */

const ESPERA_ESCRITURA = 700;
const SONDEO_MS = 4000;
const REINTENTO_MAX_MS = 30000;

const hayNombre = (ops) => ops.some((o) => o.op === 'set' && o.ruta === 'nombre');

const detalle = (err, defecto) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d.mensaje === 'string') return d.mensaje;
  return defecto;
};

export default function useSincronizacion(id) {
  const [datos, setDatos] = useState(null); // {archivo, formulario, mi_rol, puede_editar, presencia, avisos}
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null); // {status, mensaje}
  const [guardado, setGuardado] = useState('guardado'); // guardado | pendiente | guardando | reintentando

  const servidor = useRef(null); // última versión CONFIRMADA por el servidor
  const pendientes = useRef([]); // operaciones aplicadas aquí y aún sin mandar
  const enVuelo = useRef(false);
  const reloj = useRef(null);
  const reintentos = useRef(0);
  const foco = useRef(null);
  const montado = useRef(true);
  const enviarRef = useRef(null);

  const programar = useCallback((ms) => {
    clearTimeout(reloj.current);
    reloj.current = setTimeout(() => enviarRef.current?.(), ms);
  }, []);

  const recargar = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) setCargando(true);
    try {
      const { data } = await api.get(`/forms/${id}`);
      servidor.current = data.formulario;
      if (!montado.current) return;
      setDatos((previo) => {
        // Lo escrito aquí y aún sin confirmar no se pierde al releer
        const formulario = aplicarOps(data.formulario, pendientes.current);
        const nombre = hayNombre(pendientes.current) && previo?.archivo ? previo.archivo.name : data.archivo?.name;
        return { ...data, archivo: { ...data.archivo, name: nombre }, formulario };
      });
      setErrorCarga(null);
    } catch (err) {
      if (!silencioso && montado.current) {
        setErrorCarga({ status: err.response?.status, mensaje: detalle(err, 'No se pudo abrir el formulario') });
      }
    } finally {
      if (!silencioso && montado.current) setCargando(false);
    }
  }, [id]);

  const enviar = useCallback(async () => {
    clearTimeout(reloj.current);
    if (enVuelo.current || !pendientes.current.length || !servidor.current) return;
    const ops = compactar(pendientes.current);
    pendientes.current = [];
    enVuelo.current = true;
    setGuardado('guardando');
    try {
      const { data } = await api.post(`/forms/${id}/cambios`, {
        base_rev: servidor.current.rev,
        ops,
        foco: foco.current,
      });
      servidor.current = data.formulario;
      reintentos.current = 0;
      enVuelo.current = false;
      if (montado.current) {
        setDatos((d) => d && { ...d, formulario: aplicarOps(data.formulario, pendientes.current) });
      }
      if (pendientes.current.length) programar(ESPERA_ESCRITURA);
      else setGuardado('guardado');
    } catch (err) {
      enVuelo.current = false;
      const st = err.response?.status;
      if (st && st >= 400 && st < 500 && ![408, 409, 429].includes(st)) {
        // El servidor rechazó el lote (permiso retirado, dato inválido): reintentarlo repetiría el
        // error para siempre. Se descarta lo pendiente y se vuelve a lo que tiene el servidor.
        pendientes.current = [];
        setGuardado('guardado');
        toast.error(detalle(err, 'No se pudieron guardar los últimos cambios'));
        if (st === 403 && montado.current) setDatos((d) => d && { ...d, puede_editar: false });
        recargar({ silencioso: true });
        return;
      }
      // Sin red, 5xx o conflicto: se devuelven a la cola, delante de lo escrito después, y se reintenta
      pendientes.current = [...ops, ...pendientes.current];
      reintentos.current += 1;
      setGuardado('reintentando');
      programar(Math.min(REINTENTO_MAX_MS, 1000 * 2 ** reintentos.current));
    }
  }, [id, programar, recargar]);
  enviarRef.current = enviar;

  /** Aplica operaciones aquí y las encola. `inmediato`: cambios de estructura (añadir, borrar, mover). */
  const aplicar = useCallback((ops, { inmediato = false } = {}) => {
    if (!ops?.length) return;
    setDatos((d) => {
      if (!d) return d;
      const nombre = ops.filter((o) => o.op === 'set' && o.ruta === 'nombre').pop();
      const archivo = nombre ? { ...d.archivo, name: nombre.valor } : d.archivo;
      return { ...d, archivo, formulario: aplicarOps(d.formulario, ops) };
    });
    pendientes.current.push(...ops);
    if (reintentos.current > 0) return; // ya hay un reintento programado: ese se lleva también estas
    setGuardado('pendiente');
    programar(inmediato ? 0 : ESPERA_ESCRITURA);
  }, [programar]);

  /** Cambios que no pasan por `cambios` (publicación, avisos): se aplican a las dos copias. */
  const parchear = useCallback((fn) => {
    if (servidor.current) servidor.current = fn(servidor.current);
    setDatos((d) => d && { ...d, formulario: fn(d.formulario) });
  }, []);

  const setFoco = useCallback((valor) => {
    foco.current = valor || null;
  }, []);

  /*
   * Al ocultar o cerrar la pestaña se manda lo pendiente con `keepalive` (sobrevive al cierre).
   * sendBeacon no sirve: no admite la cabecera Authorization.
   */
  const vaciar = useCallback(() => {
    clearTimeout(reloj.current);
    if (!pendientes.current.length || enVuelo.current || !servidor.current) return;
    const { access } = getTokens();
    if (!access) return;
    const ops = compactar(pendientes.current);
    pendientes.current = [];
    enVuelo.current = true;
    const devolver = () => {
      enVuelo.current = false;
      pendientes.current = [...ops, ...pendientes.current];
      if (montado.current) programar(2000);
    };
    try {
      fetch(`${API}/forms/${id}/cambios`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${access}` },
        body: JSON.stringify({ base_rev: servidor.current.rev, ops, foco: null }),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r)))
        .then((data) => {
          enVuelo.current = false;
          servidor.current = data.formulario;
          if (!montado.current) return;
          if (pendientes.current.length) programar(ESPERA_ESCRITURA);
          else setGuardado('guardado');
        })
        .catch(devolver);
    } catch {
      devolver();
    }
  }, [id, programar]);

  useEffect(() => {
    montado.current = true;
    recargar();
    return () => {
      montado.current = false;
    };
  }, [recargar]);

  // Sondeo: presencia, respuestas nuevas, nombre y cambios de otros editores
  useEffect(() => {
    let parado = false;
    const tick = async () => {
      if (document.hidden || !servidor.current) return;
      try {
        const { data } = await api.get(`/forms/${id}/estado`, { params: { foco: foco.current || undefined } });
        if (parado || !montado.current) return;
        if (servidor.current) servidor.current = { ...servidor.current, respuestas_total: data.respuestas_total };
        setDatos((d) => d && {
          ...d,
          presencia: data.presencia || [],
          archivo: data.nombre && !hayNombre(pendientes.current) ? { ...d.archivo, name: data.nombre } : d.archivo,
          formulario: { ...d.formulario, respuestas_total: data.respuestas_total ?? d.formulario.respuestas_total },
        });
        if (data.rev > (servidor.current?.rev ?? 0) && !pendientes.current.length && !enVuelo.current) {
          recargar({ silencioso: true });
        }
      } catch {
        /* el sondeo falla en silencio: si no hay red, el guardado ya lo dice */
      }
    };
    const intervalo = setInterval(tick, SONDEO_MS);
    const alCambiarVisibilidad = () => {
      if (document.hidden) vaciar();
      else tick();
    };
    const alSalir = (e) => {
      if (!pendientes.current.length && !enVuelo.current) return;
      vaciar();
      // Si ni siquiera se pudo mandar (sin red), que el navegador pregunte antes de perderlo
      if (reintentos.current > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    window.addEventListener('beforeunload', alSalir);
    return () => {
      parado = true;
      clearInterval(intervalo);
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      window.removeEventListener('beforeunload', alSalir);
    };
  }, [id, recargar, vaciar]);

  // Al salir del editor dentro del portal (sin cerrar la pestaña) también se manda lo pendiente
  useEffect(() => () => {
    clearTimeout(reloj.current);
    vaciar();
  }, [vaciar]);

  return { datos, setDatos, cargando, errorCarga, guardado, aplicar, parchear, recargar, setFoco };
}
